import { api } from "./api";
import { toast } from "sonner";

const RZP_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/**
 * Ensure the Razorpay Checkout script is loaded.
 * - If `window.Razorpay` is already available, resolve immediately.
 * - Otherwise wait for the tag baked into index.html, and if it's missing
 *   (some browsers block it) inject a new one dynamically.
 * Times out after 20s with a clear error.
 */
const ensureRazorpay = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();

  // Inject the tag if it's not already in the DOM
  const existing = Array.from(document.scripts).find((s) => s.src && s.src.startsWith(RZP_SRC));
  if (!existing) {
    const tag = document.createElement("script");
    tag.src = RZP_SRC;
    tag.async = true;
    tag.onerror = () => reject(new Error("Could not download Razorpay checkout script. Please check your network / ad-blocker."));
    document.body.appendChild(tag);
  }

  const start = Date.now();
  const timer = setInterval(() => {
    if (window.Razorpay) { clearInterval(timer); return resolve(); }
    if (Date.now() - start > 20000) {
      clearInterval(timer);
      return reject(new Error("Razorpay is taking too long to load. Please refresh and try again."));
    }
  }, 120);
});

/**
 * Open the Razorpay checkout for the given package.
 * intent: 'subscription' (first payment) | 'renewal'
 */
export async function openRazorpay({ package_id, intent = "subscription", onSuccess, onDismiss }) {
  try {
    await ensureRazorpay();
    const { data: order } = await api.post("/payments/razorpay/create-order", { package_id, intent });

    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "Traveloble",
      description: order.plan_name,
      order_id: order.order_id,
      prefill: order.prefill,
      theme: { color: "#0F1E5B" },
      modal: {
        ondismiss: () => { onDismiss && onDismiss(); },
      },
      handler: async (resp) => {
        try {
          const { data } = await api.post("/payments/razorpay/verify", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          toast.success(intent === "renewal" ? "Renewed. Thank you!" : "Payment successful. Welcome aboard!");
          onSuccess && onSuccess(data);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "We received your payment but couldn't verify it. Please contact support.");
        }
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", (resp) => {
      toast.error(resp?.error?.description || "Payment failed. Please try again.");
    });
    rzp.open();
  } catch (err) {
    console.error("Razorpay open failed:", err);
    toast.error(err?.response?.data?.detail || err?.message || "Could not open checkout");
  }
}
